if (!customElements.get('quick-cart-drawer')) {
  class QuickCartDrawer extends HTMLElement {
    constructor() {
      super();
      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', this.init.bind(this));
        this.parentElement.addEventListener('shopify:section:select', () => this.open());
        this.parentElement.addEventListener('shopify:section:deselect', () => this.close());
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.toggleState = false;

      if (!this.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }

      this.querySelector('.button--close').addEventListener('click', this.close.bind(this));
      this.querySelector('.quick-cart-drawer__backdrop').addEventListener('click', this.close.bind(this));

      this.querySelector('.quick-cart-drawer__blocks').addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.toggleState) {
          this.close();
        }

        if (this.toggleState) {
          const focusableElements = this.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          const firstFocusableElement = focusableElements[0];
          const lastFocusableElement = focusableElements[focusableElements.length - 1];

          let isTabPressed = e.key === 'Tab';

          if (!isTabPressed) {
            return;
          }

          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              e.preventDefault();
              lastFocusableElement.focus();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              e.preventDefault();
              firstFocusableElement.focus();
            }
          }
        }
      });

      this.initTriggers();
    }

    initTriggers() {
      if (this.triggerHandler) return;
      this.triggerHandler = event => {
        const trigger = event.target.closest('.quick-cart-drawer__trigger');
        if (!trigger || trigger.disabled) return;
        event.preventDefault();
        this.fetchProductForQuickCartDrawer(event);
      };
      document.addEventListener('click', this.triggerHandler);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.triggerHandler);
      this.triggerHandler = null;
      this.requestId = (this.requestId || 0) + 1;
      this.sliderInstance?.destroy(true, true);
    }

    async fetchProductForQuickCartDrawer(event) {
      const trigger = event.target.closest('.quick-cart-drawer__trigger[data-product-url]');
      if (!trigger) return;
      const sourceCard = trigger.closest('product-card');
      const variantId = sourceCard?.querySelector('input[name="id"]')?.value;
      const productUrl = new URL(trigger.dataset.productUrl, window.location.origin);
      if (productUrl.origin !== window.location.origin) return;
      const localeRoot = window.Shopify?.routes?.root || '/';
      if (localeRoot !== '/' && !productUrl.pathname.startsWith(localeRoot)) {
        productUrl.pathname = localeRoot.replace(/\/$/, '') + productUrl.pathname;
      }
      if (variantId) productUrl.searchParams.set('variant', variantId);
      const requestUrl = new URL(productUrl);
      requestUrl.searchParams.set('view', 'quick-cart');
      const requestId = this.requestId = (this.requestId || 0) + 1;
      trigger.classList.add('is--loading');

      try {
        const response = await fetch(requestUrl.toString());
        if (!response.ok) throw new Error('Unable to load quick cart');
        const responseHTML = await response.text();
        if (requestId !== this.requestId) return;
        const container = document.createElement('div');
        container.innerHTML = responseHTML;
        const product = container.querySelector('.quick-cart-product');
        if (!product) throw new Error('Quick cart product not found');

        this.sliderInstance?.destroy(true, true);
        this.querySelector('.quick-cart-drawer__main').replaceChildren(product);
        const quickCard = product.querySelector('product-card');
        quickCard?.updateVariantStatuses();
        quickCard?.updateFormVariantIdInput();
        quickCard?.disableAddToCartButons();

        if (typeof Swiper !== 'undefined') {
          this.sliderInstance = new Swiper(this.querySelector('.quick-cart-drawer__media-swiper'), {
            slidesPerView: 1.25,
            spaceBetween: 8,
            freeMode: { enabled: true },
            breakpoints: { 750: { slidesPerView: 2 } }
          });
        }

        product.querySelectorAll('.variant-option-radio-input').forEach(input => {
          input.addEventListener('change', event => {
            const fieldset = event.target.closest('fieldset');
            const label = fieldset?.querySelector('[data-selected-variant]');
            if (label) label.textContent = event.target.value;
            const card = event.target.closest('product-card');
            const selectedId = card?.querySelector('input[name="id"]')?.value;
            const media = card?.variantsObj?.find(variant => String(variant.id) === selectedId)?.featured_media;
            if (media) this.setActiveMedia(media.id);
          });
        });

        const sourceSlider = trigger.closest('card-product-slider');
        if (sourceSlider?.slider?.autoplay) {
          sourceSlider.slider.autoplay.stop();
          sourceSlider.classList.add('product--open-on-quick-cart');
        }
        this.opener = trigger;
        this.open();
        this.toggleAriaExpanded(event);
      } catch (error) {
        if (requestId === this.requestId) window.location.assign(productUrl.toString());
      } finally {
        trigger.classList.remove('is--loading');
      }
    }

    setActiveMedia(id) {
      const mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(media => Number(media.dataset.mediaId) === id);
      if (mediaFound) {
        this.sliderInstance?.slideTo(Number(mediaFound.dataset.index));
      }
    }

    /** Drawer coreß functions */
    toggle() {
      if (!this.toggleState) {
        this.open();
      } else {
        this.close();
      }
    }

    open() {
      this.toggleState = true;
      document.querySelector('body').classList.add('overflow-hidden');
      const quickCartDrawer = document.querySelector('.quick-cart-drawer__blocks');
      const closeButton = quickCartDrawer.querySelector('.button--close');
      quickCartDrawer.setAttribute('tabindex', '0');
      closeButton.setAttribute('tabindex', '0');
      this.classList.add('is--open');
      this.opened();

      const firstFocusableElement = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }

    close() {
      this.toggleState = false;

      const shopTheLookDrawer = document.querySelector('shop-the-look-drawer');

      if (shopTheLookDrawer && !shopTheLookDrawer.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }
      if (!shopTheLookDrawer) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }
      this.classList.remove('is--open');
      this.closed();
      this.toggleAriaExpanded();
      this.opener?.focus();

      /** start autoplay on quick cart drawe close */
      if (document.querySelector('card-product-slider.product--open-on-quick-cart')) {
        document.querySelector('card-product-slider.product--open-on-quick-cart').slider?.autoplay?.start();
        document.querySelector('card-product-slider.product--open-on-quick-cart').classList.remove('product--open-on-quick-cart');
      }
    }

    toggleAriaExpanded(event) {
      if (event) {
        if (event.target.closest('button')) event.target.closest('button').setAttribute('aria-expanded', true);
        this.querySelector('.button--close').setAttribute('aria-expanded', true);
      } else {
        document.querySelectorAll('[aria-controls="quick-cart-drawer"]').forEach(button => {
          button.setAttribute('aria-expanded', false);
        });
      }
    }

    opened() {
      const openedEvent = new Event('opened', { bubbles: true });
      this.dispatchEvent(openedEvent);
    }

    closed() {
      const closedEvent = new Event('closed', { bubbles: true });
      this.dispatchEvent(closedEvent);
    }
  }

  customElements.define('quick-cart-drawer', QuickCartDrawer);
}
